import { makeAutoObservable } from 'mobx';
import { v4 as uuidV4 } from 'uuid';

import { isLocalEnv, isLocalOrTestEnv } from '@utils/featureFlags';

export interface TaskPayload<ReturnT = unknown> {
  /** descriptive, readable but identifiable name for a task */
  id?: string;
  /** task action */
  action: () => Promise<ReturnT>;
}

class Task<ReturnT = unknown> implements TaskPayload<ReturnT> {
  private onFinish: (id: string) => void;
  private promise: Promise<ReturnT> | undefined;

  id: string;
  action: () => Promise<ReturnT>;
  isFinished = false;

  constructor(
    id: string | undefined,
    action: () => Promise<ReturnT>,
    onFinish: (id: string) => void
  ) {
    this.id = id ?? `[Task]-${uuidV4()}`;
    this.action = action;
    this.onFinish = onFinish;
  }

  /** start running task */
  async start() {
    // don't start same task multiple times
    if (this.promise) return this.promise;

    this.promise = this.action()
      .catch((err) => {
        if (isLocalOrTestEnv) {
          console.error(`[TaskStore] ${this.id} failed`, err);
        }

        throw err;
      })
      .finally(() => {
        if (isLocalEnv) console.info(`[TaskStore] ${this.id} finished`);

        this.isFinished = true;
        this.onFinish(this.id);
      });

    return this.promise;
  }
}

/** Global state for managing unfinished processes that require
 * displaying a global loading state styles/animation and prevent
 * further user from interacting with the page */
export class TaskStore {
  tasks: Task[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Whether all tasks are finished and cleared */
  get isReady() {
    return this.tasks.length === 0;
  }

  /** Add task to queue and return task instance for starting the task */
  addTask = <ReturnT>({ id, action }: TaskPayload<ReturnT>) => {
    // if there's already a task running with the same id, return it
    const existingTask = this.tasks.find(
      (task) => task.id === id
    ) as Task<ReturnT>;

    if (existingTask) return existingTask;

    const task = new Task<ReturnT>(id, action, this.cleanFinishedTasks);

    // add task to queue
    this.tasks.push(task);

    if (isLocalEnv) {
      console.info(
        '[TaskStore] New task added, open tasks:',
        this.tasks.map(({ id }) => id)
      );
    }

    return task;
  };

  addTaskAndRun = <ReturnT>(params: TaskPayload<ReturnT>) => {
    return this.addTask(params).start();
  };

  /** Remove task queue. NOTE: task is not cancelled */
  private cleanFinishedTasks() {
    this.tasks = this.tasks.filter((task) => !task.isFinished);
  }

  /** Clear task queue */
  reset() {
    this.tasks = [];
  }
}

/** Utility for running an async action in task queue and thereby displaying a
 * global loading animation while the task is unfinished  */
export const createAsyncTask = <T extends (...args: any[]) => Promise<any>>(
  taskId: string,
  action: T
) => {
  return (...params: Parameters<T>) =>
    taskStore
      .addTask<
        Awaited<ReturnType<T>>
      >({ id: taskId, action: () => action(...params) })
      .start();
};

export const taskStore = new TaskStore();
